import { Dropdown, Loader } from '@sam/components';
import { useEffect, useMemo } from 'react';
import { MonthKey, MonthlyTimes } from 'shared';
import i18n from '../../i18n/i18n';
import {
  convertNumberOfMinutesToString,
  generateDropdownOptionsForYear,
} from '../../utils/timeTracking/TimeTracking.utils';
import { IconComponent, IconType } from '../icons/IconComponent';

export const GenerateMonthlyOverviewBoxes: React.FC<{
  monthlyTimes: MonthlyTimes | undefined;
  selectedMonthAndYear: [number, number];
  setSelectedMonthAndYear: (selected: [number, number]) => void;
  isLoading?: boolean;
}> = ({ monthlyTimes, selectedMonthAndYear, setSelectedMonthAndYear, isLoading }) => {
  //Check for values in localStorage
  useEffect(() => {
    const month: string | null = localStorage.getItem('selectedMonth');
    const year: string | null = localStorage.getItem('selectedYear');
    if (month && year && !isNaN(Number(month)) && !isNaN(Number(year))) {
      setSelectedMonthAndYear([Number(month), Number(year)]);
    }
  }, []);

  /**
   * Array of the created boxes for each month
   */
  const boxes: React.JSX.Element[] = useMemo((): React.JSX.Element[] => {
    const monthNow: number = new Date().getMonth();
    const list: React.JSX.Element[] = [];
    for (let curMonth = 0; curMonth <= 11; curMonth++) {
      list.push(
        <div
          key={`monthly-times-${curMonth}`}
          onClick={() => {
            localStorage.setItem('selectedMonth', curMonth.toString());
            localStorage.setItem('selectedYear', selectedMonthAndYear[1].toString());
            setSelectedMonthAndYear([curMonth, selectedMonthAndYear[1]]);
          }}
          className={[
            'timetracking-overview-monthly__boxes',
            monthNow >= curMonth ? 'past-month__box' : 'future-month__box',
            selectedMonthAndYear[0] === curMonth ? 'active-month__box' : '',
          ].join(' ')}
        >
          <div
            className={[
              'timetracking-overview-monthly__boxes__month',
              monthNow >= curMonth ? 'past-month' : 'future-month',
              selectedMonthAndYear[0] == curMonth ? 'active-month' : '',
            ].join(' ')}
          >
            <p className={'timetracking-overview-monthly__boxes__month-shortname'}>
              {i18n.t(`pages.timekeeping.months.${curMonth}`)}
            </p>
          </div>
          <p className="timetracking-overview-monthly__boxes__total-worktime">
            {convertNumberOfMinutesToString(
              monthlyTimes ? monthlyTimes[curMonth.toString() as MonthKey] : 0,
            )}
          </p>
        </div>,
      );
    }
    return list;
  }, [selectedMonthAndYear, monthlyTimes, setSelectedMonthAndYear]);

  /**
   * Helper to quickly et the selected month and year to the current date
   */
  const selectToday = (): void => {
    const dateNow: Date = new Date();
    const month: string = dateNow.getMonth().toString();
    const year: string = dateNow.getFullYear().toString();
    localStorage.setItem('selectedMonth', month);
    localStorage.setItem('selectedYear', year);
    setSelectedMonthAndYear([Number(month), Number(year)]);
  };
  return (
    <div className="timetracking-overview-monthly__wrapper">
      {isLoading ? (
        <Loader fill />
      ) : (
        <>
          <p className="timetracking-overview-monthly__headline">
            {i18n.t('pages.timekeeping.boxesHeadline')}
          </p>
          <div className="timetracking-overview-monthly__options">
            <Dropdown
              onChange={(year) => {
                localStorage.setItem('selectedYear', year);
                setSelectedMonthAndYear([selectedMonthAndYear[0], Number(year)]);
              }}
              selectedOption={selectedMonthAndYear[1].toString()}
              options={generateDropdownOptionsForYear()}
            />
            <IconComponent
              onClick={selectToday}
              type={IconType.TODAY}
              className="timetracking-overview-monthly__reset"
            />
          </div>
          <div className="timetracking-overview-monthly__boxes-container">{boxes}</div>
        </>
      )}
    </div>
  );
};
